import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useState } from 'react'
import { useUser } from '../../contexts/UserContext'
import { userService } from '../../services/userService'
import OnboardingFooter from './OnboardingFooter'
import OnboardingHeader from './OnboardingHeader'
import OnboardingInput from './OnboardingInput'

interface OnboardingStepPersonalInfoProps {
  onNext: () => void
  onBack: () => void
}

interface FormData {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
}

// Format phone number to work with django-phonenumber-field
const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  // If the number starts with '1', remove it
  const withoutCountryCode = digits.startsWith('1') ? digits.slice(1) : digits

  // Ensure we have exactly 10 digits
  if (withoutCountryCode.length !== 10) {
    throw new Error('Phone number must be 10 digits')
  }

  // Format as +1XXXXXXXXXX for django-phonenumber-field
  return `+1${withoutCountryCode}`
}

// Format phone number for display (e.g., (123) 456-7890)
const formatPhoneNumberForDisplay = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  // If the number starts with '1', remove it
  const withoutCountryCode = digits.startsWith('1') ? digits.slice(1) : digits

  // Format as (XXX) XXX-XXXX
  if (withoutCountryCode.length === 10) {
    return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`
  }

  return phone
}

const OnboardingStepPersonalInfo: React.FC<OnboardingStepPersonalInfoProps> = ({
  onNext,
  onBack,
}) => {
  const { user } = useUser()
  const [formData, setFormData] = useState<FormData>({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    phoneNumber: user?.phone_number
      ? formatPhoneNumberForDisplay(user.phone_number)
      : '',
    email: user?.email || '',
  })
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)

  const handleInputChange =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setFormData((prev) => ({ ...prev, [field]: value }))

      // Validate phone number on change
      if (field === 'phoneNumber') {
        try {
          // Only validate if we have enough digits
          if (value.replace(/\D/g, '').length >= 10) {
            formatPhoneNumber(value)
            setPhoneError(null)
          }
        } catch (err) {
          setPhoneError(
            err instanceof Error ? err.message : 'Invalid phone number format',
          )
        }
      }
    }

  const handleProfileImageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        setProfileImage(file)
      }
    },
    [],
  )

  const handleSubmit = async () => {
    if (!user) return

    try {
      setIsSubmitting(true)
      setError(null)
      setPhoneError(null)

      // Validate phone number before submission
      const formattedPhone = formatPhoneNumber(formData.phoneNumber)

      // Update user profile
      await userService.updateUser(user.id, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formattedPhone,
        email: formData.email,
      })

      // Upload profile image if selected
      if (profileImage) {
        await userService.updateProfileImage(user.id, profileImage)
      }

      onNext()
    } catch (err) {
      if (err instanceof Error && err.message.includes('phone number')) {
        setPhoneError(err.message)
      } else {
        setError(
          err instanceof Error ? err.message : 'Failed to update profile',
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={css(styles.root)}>
      <div className={css(styles.content)}>
        <OnboardingHeader title="Personal Information" progress={33} />
        <div className={css(styles.profilePhotoSection)}>
          <div className={css(styles.profilePhotoCircle)}>
            <div className={css(styles.profilePhotoInner)}>
              {profileImage ? (
                <img
                  src={URL.createObjectURL(profileImage)}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <img
                  src="/profile_icon.png"
                  alt="Profile"
                  style={{ width: 40, height: 40 }}
                />
              )}
              <div className={css(styles.profilePhotoLabel)}>Profile Photo</div>
            </div>
            <label className={css(styles.addPhotoIcon)}>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                style={{ display: 'none' }}
              />
              <span className={css(styles.plusNudge)}>+</span>
            </label>
          </div>
        </div>
        <div className={css(styles.formGroup)}>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="First Name"
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              placeholder="First Name"
              name="firstName"
            />
          </div>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="Last Name"
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              placeholder="Last Name"
              name="lastName"
            />
          </div>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={handleInputChange('phoneNumber')}
              placeholder="(123) 456-7890"
              name="phoneNumber"
              type="tel"
            />
            {phoneError && (
              <div className={css(styles.fieldError)}>{phoneError}</div>
            )}
          </div>
          <div className={css(styles.inputContainer)}>
            <OnboardingInput
              label="Email Address"
              value={formData.email}
              onChange={handleInputChange('email')}
              placeholder="Email Address"
              name="email"
              type="email"
            />
          </div>
        </div>
        {error && <div className={css(styles.error)}>{error}</div>}
      </div>
      <OnboardingFooter
        onNext={handleSubmit}
        onBack={onBack}
        nextText={isSubmitting ? 'Saving...' : 'Next'}
        disabled={!!phoneError || isSubmitting}
      />
    </div>
  )
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: 'clamp(12px, 2vw, 24px)',
    width: '100%',
    margin: '0 auto clamp(16px, 3vw, 32px) auto',
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      gap: '24px 16px',
    },
  },
  inputContainer: {
    width: '100%',
    '@media (min-width: 1024px)': {
      width: '48%',
    },
  },
  profilePhotoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 'clamp(16px, 3vw, 32px)',
    position: 'relative',
    width: '100%',
    maxWidth: 'min(100%, 340px)',
    alignSelf: 'center',
  },
  profilePhotoCircle: {
    width: 'clamp(80px, 12vw, 120px)',
    height: 'clamp(80px, 12vw, 120px)',
    borderRadius: '50%',
    background: '#F6F6F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
  },
  profilePhotoInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profilePhotoLabel: {
    fontSize: 'clamp(10px, 1vw, 14px)',
    fontWeight: 400,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 4,
  },
  addPhotoIcon: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    background: '#2563EB',
    color: '#fff',
    borderRadius: '50%',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    border: '2px solid #fff',
    cursor: 'pointer',
  },
  plusNudge: {
    position: 'relative',
    top: '-1px',
    left: '0px',
  },
  error: {
    color: '#DC2626',
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  fieldError: {
    color: '#DC2626',
    fontSize: 12,
    marginTop: 4,
  },
})

export default OnboardingStepPersonalInfo
